import { submitEventMerge, submitMerge } from "@otto-ec/tracking-bct";
import { promoComboPresentation } from "@otto-ec/nexus-registry/promo/combo/presentation";

const LARGE_VIEWPORT_WIDTH = 1400;

const SHEET_ID_RADIX = 36;
const SHEET_ID_RANDOM_LENGTH = 10;
export default class ComboPromoButton {
	articleNumber = null;
	imageIdToCombo = null;
	destinationDomElement = null;
	promoComboPresentation = null;

	constructor() {
		this.initialize();
	}

	initialize() {
		this.promoComboPresentation = promoComboPresentation;
		promoComboPresentation.variationChange.on(data => {
			this.loadButton(
				data.articleNumber,
				data.targetElement,
				data.imageId,
				data.imagePreset,
				data.eventMergeId,
				data.variationId
			);
		});
		promoComboPresentation.imageChange.on(data => this.update(data.imageId));
		window.otto.ft3bcn.combopromo.updateButton.on(data => {
			if (data.json) this.updateButton(data.json, data.eventMergeId, data.imageId);
		});
	}

	async loadButton(articleNumber, destinationDomElement, imageId, _preset, eventMergeId, variationId) {
		this.variationId = variationId;
		this.articleNumber = articleNumber;
		this.destinationDomElement = destinationDomElement;

		if (imageId && typeof imageId !== "string") throw new Error("imageId has to be a string");

		const STATUS_CODE_OK = 200;
		let url = `/combo-combopromo/button/${articleNumber}`;
		if (imageId) url += `?imageId=${encodeURIComponent(imageId)}`;

		if (articleNumber) {
			try {
				await fetch(url, {
					method: "GET",
					headers: {
						accept: "application/json"
					}
				}).then(async response => {
					if (response.status === STATUS_CODE_OK) {
						try {
							await response.json().then(json =>
								window.otto.ft3bcn.combopromo.updateButton.emit({
									json: json,
									eventMergeId: eventMergeId,
									imageId: imageId
								})
							);
						} catch (exception) {
							if (exception instanceof TypeError || exception.name === "AbortError") throw exception;
							throw new Error(`Output cannot be parsed: url ${url} Status code: ${response.status}
                               And response: ${await response.text()}.With exception: ${exception}`, { cause: exception });
						}
					}
				});
			} catch (exception) {
				if (!(exception instanceof TypeError || exception.name === "AbortError")) {
					throw new Error(`Failed to fetch url ${url} : ${exception.name} : ${exception.message}`, {
						cause: exception
					});
				}
			}
		}
	}

	updateButton(json, eventMergeId, imageId) {
		this.imageIdToCombo = json.imageIdToCombo;
		this.comboType = json.comboType || "OUTFIT";
		this.title = json.title;
		this.cssComboTypeImageClass = json.cssComboTypeImageClass;
		this.cssFormatImageClass = json.cssFormatImageClass;
		this.cssFormatButtonClass = json.cssFormatButtonClass;
		this.toggles = json.toggles || {};

		const usedImageId = this.imageExists(imageId) ? imageId : json.newestComboImageId;

		if (this.imageExists(usedImageId)) {
			this.injectButtonHtml(usedImageId);
			const comboId = this.imageIdToCombo[usedImageId].comboId;
			const source = this.imageIdToCombo[usedImageId].source;

			if (eventMergeId) {
				submitEventMerge(eventMergeId, this.buildInitialViewTracking());
				window.o_p13n_combopromo.tracking.sendPromoButtonMergePageImpression(
					comboId,
					source,
					this.comboType,
					eventMergeId,
					this.variationId
				);
			} else {
				submitMerge(this.buildInitialViewTracking());
				window.o_p13n_combopromo.tracking.sendPromoButtonPageImpressionTrackingEvent(
					comboId,
					source,
					this.comboType,
					window.o_p13n_combopromo.api.variationId
				);
			}
		}
	}

	update(imageId) {
		if (!this.imageExists(imageId)) return;
		this.injectButtonHtml(imageId);
	}

	imageExists(imageId) {
		return this.imageIdToCombo?.[imageId];
	}

	injectButtonHtml(imageId) {
		const comboLink = this.buildHrefLinkFor(imageId);
		const cssFormatButtonClass = this.cssFormatButtonClass;
		const imagePreset = this.comboType === "ROOM" ? "poi_room" : "poi_outfit";
		const imageUrl = `https:/*                                            */
		const comboId = this.imageIdToCombo[imageId].comboId;
		const source = this.imageIdToCombo[imageId].source;

		this.destinationDomElement.innerHTML = this.isModalActive()
			? this.generateComboPromoButtonHtmlWithModal(comboLink, imageUrl, comboId)
			: this.generateComboPromoButtonHtml(comboLink, imageUrl);

		const comboButton = this.destinationDomElement.querySelector(`.${cssFormatButtonClass}`);
		this.isModalActive()
			? this.addTrackingClickEventListener(comboButton, comboId, source)
			: this.oldAddTrackingClickEventListener(comboButton, comboId, source);
	}

	oldAddTrackingClickEventListener(comboButton, comboId, source) {
		comboButton?.addEventListener("click", () => {
			window.o_p13n_combopromo.tracking.sendPromoButtonClickTrackingEvent(
				comboId,
				source,
				this.comboType,
				this.variationId
			);
		});
	}

	addTrackingClickEventListener(comboButton, comboId, source) {
		comboButton?.addEventListener("click", () => {
			window.o_p13n_combopromo.tracking.sendPromoButtonOpenTrackingEvent(
				comboId,
				source,
				this.comboType,
				this.variationId
			);
		});
	}

	isModalActive() {
		try {
			return JSON.parse(this.toggles)["ENABLE_COMBO_MODAL"] === true;
		} catch {
			return false;
		}
	}

	generateComboPromoButtonHtml(comboLink, imageUrl) {
		return `<div class="combo_combopromo_button_wrapper" data-toggles='${this.toggles}'>
                    <oc-card-v2 oc-aria-label="${this.title}" size="50">
                        <a class="${this.cssFormatButtonClass}"
                            aria-label="${this.title}"
                            href="${comboLink}">
                            <picture>
                                <source srcset="${imageUrl + "&amp;fmt=auto"}">
                                <img class="${this.cssComboTypeImageClass}  ${this.cssFormatImageClass}"
                                     src="${imageUrl}" alt="" aria-hidden="true">
                             </picture>
                             <span class="combo_combopromo_button_text_wrapper">
                                 <span class="combo_combopromo_button_text oc-copy-125--m oc-m-50">${this.title}</span>
                             </span>
                         </a>
                    </oc-card-v2>
                </div>`.replace(/\n\s*/gm, "");
	}

	generateComboPromoButtonHtmlWithModal(comboLink, imageUrl, comboId) {
		const sheetId = `combo-modal-${comboId}-${Math.random().toString(SHEET_ID_RADIX).slice(2, SHEET_ID_RANDOM_LENGTH)}`;
		const presentationUrl = `/combo-presentation/modal/combos/${comboId}`;

		const isWideViewport = window.innerWidth > LARGE_VIEWPORT_WIDTH;
		const alignment = isWideViewport ? "center" : "right";

		return `<div class="combo_combopromo_button_wrapper" data-toggles='${this.toggles}'>
                    <oc-card-v2 oc-aria-label="${this.title}" size="50">
                        <a class="${this.cssFormatButtonClass}"
                            style="cursor: pointer;"
                            aria-label="${this.title}"
                            role="button"
                            tabindex="0"
							data-oc-sheet-v1-create='{"id":"${sheetId}","url":"${presentationUrl}"}' >
                            <picture>
                                <source srcset="${imageUrl + "&amp;fmt=auto"}">
                                <img class="${this.cssComboTypeImageClass}  ${this.cssFormatImageClass}"
                                     src="${imageUrl}" alt="" aria-hidden="true">
                             </picture>
                             <span class="combo_combopromo_button_text_wrapper">
                                 <span class="combo_combopromo_button_text oc-copy-125--m oc-m-50">${this.title}</span>
                             </span>
                         </a>
                    </oc-card-v2>
                    <oc-sheet-v1 id="${sheetId}" adaptive-alignment="${alignment}" max-width-centered="600px" full-height hide-header no-content-padding style="display: none;" ></oc-sheet-v1>
                </div>`.replace(/\n\s*/gm, "");
	}

	buildHrefLinkFor(imageId) {
		return `/k/${this.imageIdToCombo[imageId].comboId}?a=${this.articleNumber}`;
	}

	buildInitialViewTracking() {
		return {
			promo_ComboType: this.comboType
		};
	}
}
